/**
 * ChatWithModals - Simplified component that wraps chat with modal handling
 *
 * Uses the new CopilotChatContext for state management and modal control.
 */

import React, { useState, useMemo, useRef } from 'react';
import LocationModal from 'src/components/account/LocationModal';
import ServiceAreaModal from 'src/components/account/ServiceAreaModal';
import { ProductFormModal } from 'src/components/common/LazyComponents';
import useCatalogKinds from 'src/hooks/useCatalogKinds';
import { useCatalogsContext } from 'src/contexts/catalogs/CatalogsContext';
import CustomerProfileFormModal from 'src/components/common/CustomerProfileFormModal';
import DeleteConfirmationModal from 'src/components/common/DeleteConfirmationModal';
import { ICustomerProfile } from 'src/lib/schemas';
import useCopilotChatContext from '../hooks/useCopilotChatContext';
import {
  CopilotLocation,
  CopilotServiceArea,
  CopilotCatalog,
  CopilotCustomerProfile,
} from '../context/CopilotChatContext';

interface ChatWithModalsProps {
  children: React.ReactNode;
}

const ChatWithModals: React.FC<ChatWithModalsProps> = ({ children }) => {
  const {
    // Modal state from context
    locationModalOpen,
    serviceAreaModalOpen,
    productModalOpen,
    customerProfileModalOpen,
    setLocationModalOpen,
    setServiceAreaModalOpen,
    setProductModalOpen,
    setCustomerProfileModalOpen,

    // Modal prefill data
    locationModalPrefill,
    clearLocationModalPrefill,
    serviceAreaModalPrefill,
    clearServiceAreaModalPrefill,
    productModalPrefill,
    clearProductModalPrefill,
    customerProfileModalPrefill,
    clearCustomerProfileModalPrefill,

    // Add/remove functions from context
    addLocation,
    updateLocation,
    addServiceArea,
    updateServiceArea,
    addCatalog,
    addCustomerProfile,
    updateCustomerProfile,
    removeCustomerProfile,

    // Send message for notifications
    sendMessage,

    // Editing state from shared context (set by Message.tsx, used here)
    editingLocation,
    setEditingLocation,
    editingServiceArea,
    setEditingServiceArea,
    editingCustomerProfile,
    setEditingCustomerProfile,

    // Onboarding state for prefill data
    onboardingState,
  } = useCopilotChatContext();

  // Editing state for product (local to this component)
  const [editingProduct, setEditingProduct] = useState<CopilotCatalog | null>(null);

  // Delete confirmation modal
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState<CopilotCustomerProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Track if modal was opened for creation (for cancel notifications)
  const wasCreationModalRef = useRef(false);

  // Track previous modal state to detect opening
  const prevLocationModalOpen = useRef(locationModalOpen);
  const prevServiceAreaModalOpen = useRef(serviceAreaModalOpen);
  const prevProductModalOpen = useRef(productModalOpen);
  const prevCustomerProfileModalOpen = useRef(customerProfileModalOpen);

  // Update refs when modals open (via context actions like handleUIComponents)
  // Only mark as creation modal if no editing object is set
  React.useEffect(() => {
    if (!prevLocationModalOpen.current && locationModalOpen) {
      // Only treat as creation if not editing an existing item
      if (!editingLocation) {
        wasCreationModalRef.current = true;
      }
    }
    prevLocationModalOpen.current = locationModalOpen;
  }, [locationModalOpen, editingLocation]);

  React.useEffect(() => {
    if (!prevServiceAreaModalOpen.current && serviceAreaModalOpen) {
      // Only treat as creation if not editing an existing item
      if (!editingServiceArea) {
        wasCreationModalRef.current = true;
      }
    }
    prevServiceAreaModalOpen.current = serviceAreaModalOpen;
  }, [serviceAreaModalOpen, editingServiceArea]);

  React.useEffect(() => {
    if (!prevProductModalOpen.current && productModalOpen) {
      // Only treat as creation if not editing an existing item
      if (!editingProduct) {
        wasCreationModalRef.current = true;
      }
    }
    prevProductModalOpen.current = productModalOpen;
  }, [productModalOpen, editingProduct]);

  React.useEffect(() => {
    if (!prevCustomerProfileModalOpen.current && customerProfileModalOpen) {
      // Only treat as creation if not editing an existing item
      if (!editingCustomerProfile) {
        wasCreationModalRef.current = true;
      }
    }
    prevCustomerProfileModalOpen.current = customerProfileModalOpen;
  }, [customerProfileModalOpen, editingCustomerProfile]);

  const { catalogKinds } = useCatalogKinds();
  const { refetch: refetchCatalogs } = useCatalogsContext();

  // Handle save location
  const handleSaveLocation = async (locationData: any) => {
    try {
      // Create location in the locations collection via API
      const { createLocation, updateLocation: updateLocationApi } = await import('src/services/account');
      
      const locationPayload = {
        name: locationData.name,
        address: {
          street: locationData.address.street,
          city: locationData.address.city,
          state: locationData.address.state,
          postalCode: locationData.address.postalCode,
          country: locationData.address.country,
          lat: Number(locationData.address.lat),
          long: Number(locationData.address.long),
          placeId: locationData.address.placeId,
        },
        isDefault: locationData.isDefault,
        phone: locationData.phone,
      };

      // Check if we're editing an existing location with a valid MongoDB ID
      const editingId: any = editingLocation?.id;
      // A valid MongoDB ID is a 24-character hex string
      const isValidMongoId = editingId && /^[a-f\d]{24}$/i.test(editingId);
      
      let resultLocation;
      
      if (editingLocation && isValidMongoId) {
        // Update existing location with valid MongoDB ID
        resultLocation = await updateLocationApi(editingId, locationPayload);
      } else {
        // Create new location (for new, undefined, or scraped IDs)
        resultLocation = await createLocation(locationPayload);
      }
      
      // Use the MongoDB ID from the result
      const copilotLocation: CopilotLocation = {
        id: resultLocation._id || resultLocation.id,
        name: locationData.name,
        address: {
          street: locationData.address.street,
          city: locationData.address.city,
          state: locationData.address.state,
          postalCode: locationData.address.postalCode,
          country: locationData.address.country,
          lat: Number(locationData.address.lat),
          long: Number(locationData.address.long),
          placeId: locationData.address.placeId,
        },
        main: locationData.isDefault,
        phone_number: locationData.phone,
      };

      if (editingLocation) {
        // Replace the old location with the new/updated one (use name as fallback key if no ID)
        updateLocation(editingId || editingLocation.name, copilotLocation);
      } else {
        addLocation(copilotLocation);
      }
      closeLocationModal(true);
    } catch (error) {
      console.error('Error saving location:', error);
      const { toastError } = await import('src/services/toast');
      toastError('Failed to save location');
    }
  };

  // Handle save service area
  const handleSaveServiceArea = async (serviceAreaData: any) => {
    try {
      // Import API functions
      const { createServiceableArea, updateServiceableArea } = await import('src/services/account');
      
      let serviceAreaPayload: any;
      let copilotServiceArea: CopilotServiceArea;

      // Handle geographic type service areas
      if (serviceAreaData.type === 'geographic' && serviceAreaData.geographic) {
        serviceAreaPayload = {
          name: serviceAreaData.name,
          type: 'geographic',
          geographic: {
            entityType: serviceAreaData.geographic.entityType,
            entityId: serviceAreaData.geographic.entityId,
            entityName: serviceAreaData.geographic.entityName,
            country: serviceAreaData.geographic.country,
            state: serviceAreaData.geographic.state,
            displayName: serviceAreaData.geographic.displayName,
          },
        };
      } else {
        // Handle radius type service areas (default)
        serviceAreaPayload = {
          name: serviceAreaData.name,
          type: 'radius',
          address: {
            street: serviceAreaData.address?.street,
            city: serviceAreaData.address?.city,
            state: serviceAreaData.address?.state,
            postalCode: serviceAreaData.address?.postalCode,
            country: serviceAreaData.address?.country,
            lat: Number(serviceAreaData.address?.lat),
            long: Number(serviceAreaData.address?.long),
            placeId: serviceAreaData.address?.placeId,
          },
          radius: Number(serviceAreaData.radius),
        };
      }

      // Check if we're editing an existing service area with a valid MongoDB ID
      const editingId: any = editingServiceArea?.id;
      // A valid MongoDB ID is a 24-character hex string
      const isValidMongoId = editingId && /^[a-f\d]{24}$/i.test(editingId);
      
      let resultServiceArea;
      
      if (editingServiceArea && isValidMongoId) {
        // Update existing service area with valid MongoDB ID
        resultServiceArea = await updateServiceableArea(editingId, serviceAreaPayload);
      } else {
        // Create new service area (for new, undefined, or scraped IDs)
        resultServiceArea = await createServiceableArea(serviceAreaPayload);
      }
      
      copilotServiceArea = {
        id: resultServiceArea._id || resultServiceArea.id,
        ...serviceAreaPayload,
        address: serviceAreaPayload.address || null,
        radius: serviceAreaPayload.radius || null,
      };
      
      if (editingServiceArea) {
        // Replace the old service area with the new/updated one (use name as fallback key if no ID)
        updateServiceArea(editingId || editingServiceArea.name, copilotServiceArea);
      } else {
        addServiceArea(copilotServiceArea);
      }
      closeServiceAreaModal(true);
    } catch (error) {
      console.error('Error saving service area:', error);
      const { toastError } = await import('src/services/toast');
      toastError('Failed to save service area');
    }
  };

  // Handle save product
  const handleSaveProduct = async (productData: any) => {
    try {
      // Create new catalog in backend
      const { createAccountConfigOptions, getAccountConfigOptions } = await import('src/services/account');
      
      const payloadWithoutUrl = { ...productData };
      delete payloadWithoutUrl.url;
      
      const createdCatalog = await createAccountConfigOptions('catalogs', payloadWithoutUrl);

      console.log('[handleSaveProduct] API response:', JSON.stringify(createdCatalog, null, 2));

      // Refresh catalogs context to get fresh data
      await refetchCatalogs();

      const wasExisting = createdCatalog?.wasExisting === true;
      const nameMatches = createdCatalog?.name === productData.name;
      
      let actualCatalogId: string | undefined;
      
      if (wasExisting || !nameMatches) {
        console.warn('[handleSaveProduct] API returned existing/wrong catalog, searching by name...');
        const freshCatalogs = await getAccountConfigOptions('catalogs');
        const newCatalog: any = freshCatalogs?.find((c: any) => c.name === productData.name);
        if (newCatalog) {
          actualCatalogId = newCatalog._id || newCatalog.id;
          console.log('[handleSaveProduct] Found catalog by name:', actualCatalogId);
        }
      } else {
        actualCatalogId = createdCatalog?._id || createdCatalog?.id;
      }

      if (!actualCatalogId) {
        console.error('[handleSaveProduct] Failed to get catalog ID');
        throw new Error('Failed to create catalog - no ID returned');
      }

      console.log('[handleSaveProduct] Using catalogId:', actualCatalogId, 'for product:', productData.name);

      const copilotCatalog: CopilotCatalog = {
        catalogId: actualCatalogId,
        id: actualCatalogId,
        name: productData.name,
        description: productData.details?.description || productData.description || '',
        price: productData.details?.price || 0,
        image: productData.image || productData.details?.image || '',
        type: productData.kind || 'product',
        kind: productData.kind || 'product',
      };

      addCatalog(copilotCatalog);
      closeProductModal(true);
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  // Handle customer profile success
  const handleCustomerProfileSuccess = (profile: ICustomerProfile) => {
    const copilotProfile: CopilotCustomerProfile = {
      _id: (profile as any)._id,
      name: profile.name,
      description: profile.description || '',
      demographics: (profile as any).demographics,
      interests: (profile as any).interests,
      painPoints: (profile as any).painPoints,
      customer_rules: (profile as any).customer_rules || [],
      customer_tones: (profile as any).customer_tones || [],
    };

    // If editing, update the existing profile; otherwise add new
    if (editingCustomerProfile) {
      updateCustomerProfile(editingCustomerProfile._id, copilotProfile);
    } else {
      addCustomerProfile(copilotProfile);
    }
    closeCustomerProfileModal(true);
  };

  // Handle delete customer profile
  const handleDeleteConfirm = async () => {
    if (!profileToDelete) return;

    setIsDeleting(true);
    try {
      const [{ deleteCustomerProfile }, { toastSuccess }] = await Promise.all([
        import('src/services/account'),
        import('src/services/toast'),
      ]);

      await deleteCustomerProfile(profileToDelete._id);
      removeCustomerProfile(profileToDelete._id);
      toastSuccess(`Customer profile "${profileToDelete.name}" deleted successfully`);

      setDeleteConfirmModalOpen(false);
      setProfileToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete customer profile:', error);
      const { toastError } = await import('src/services/toast');
      toastError(`Failed to delete customer profile: ${error.message || 'Unknown error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Send cancel notification
  const sendCancelNotification = (modalType: string) => {
    if (wasCreationModalRef.current) {
      sendMessage(`${modalType} modal closed without saving.`, 'user', true, true);
    }
  };

  // Close handlers
  const closeLocationModal = (wasSaved = false) => {
    if (!wasSaved) sendCancelNotification('Location');
    setLocationModalOpen(false);
    setEditingLocation(null);
    clearLocationModalPrefill();
    wasCreationModalRef.current = false;
  };

  const closeServiceAreaModal = (wasSaved = false) => {
    if (!wasSaved) sendCancelNotification('Service area');
    setServiceAreaModalOpen(false);
    setEditingServiceArea(null);
    clearServiceAreaModalPrefill();
    wasCreationModalRef.current = false;
  };

  const closeProductModal = (wasSaved = false) => {
    if (!wasSaved) sendCancelNotification('Product');
    setProductModalOpen(false);
    setEditingProduct(null);
    clearProductModalPrefill();
    wasCreationModalRef.current = false;
  };

  const closeCustomerProfileModal = (wasSaved = false) => {
    if (!wasSaved) sendCancelNotification('Customer profile');
    setCustomerProfileModalOpen(false);
    setEditingCustomerProfile(null);
    clearCustomerProfileModalPrefill();
    wasCreationModalRef.current = false;
  };

  // Convert editing location to modal format, or prefill from context
  const locationDraft = useMemo(() => {
    if (editingLocation) {
      return {
        name: editingLocation.name,
        address: {
          street: editingLocation.address?.street || '',
          city: editingLocation.address?.city || '',
          state: editingLocation.address?.state || '',
          postalCode: editingLocation.address?.postalCode || '',
          country: editingLocation.address?.country || '',
          lat: String(editingLocation.address?.lat || ''),
          long: String(editingLocation.address?.long || ''),
          formatted: [
            editingLocation.address?.street,
            editingLocation.address?.city,
            editingLocation.address?.state,
            editingLocation.address?.postalCode,
            editingLocation.address?.country,
          ]
            .filter(Boolean)
            .join(', '),
        },
        isDefault: editingLocation.main || false,
        phone: editingLocation.phone_number || '',
      };
    }

    // For new locations, prefill from modal prefill data and/or onboarding state
    const prefillPhone = onboardingState?.phoneNumber || '';
    const prefillName = locationModalPrefill?.name || '';
    const prefillAddress = locationModalPrefill?.address || '';

    if (prefillPhone || prefillName || prefillAddress) {
      return {
        name: prefillName,
        address: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          lat: '',
          long: '',
          formatted: prefillAddress,
        },
        isDefault: false,
        phone: prefillPhone,
      };
    }

    return null;
  }, [editingLocation, onboardingState?.phoneNumber, locationModalPrefill]);

  // Convert product prefill data to ProductFormModal format
  // Note: description should be at root level, prepareInitialValues will move it to details
  const productInitialValues = useMemo(() => {
    if (editingProduct) {
      // Transform CopilotCatalog to ICatalogForm format
      return {
        name: editingProduct.name,
        kind: editingProduct.kind || 'service',
        description: editingProduct.description || '',
      };
    }
    if (productModalPrefill) {
      return {
        name: productModalPrefill.name || '',
        kind: productModalPrefill.type || 'service',
        url: productModalPrefill.url || '',
        description: productModalPrefill.description || '',
      };
    }
    return undefined;
  }, [editingProduct, productModalPrefill]);

  // Convert customer profile prefill data to modal format
  const customerProfileInitialValues = useMemo(() => {
    if (customerProfileModalPrefill) {
      return {
        name: customerProfileModalPrefill.name || '',
        description: customerProfileModalPrefill.description || '',
        // Legacy fields (for backwards compatibility)
        demographics: customerProfileModalPrefill.demographics,
        interests: customerProfileModalPrefill.interests,
        painPoints: customerProfileModalPrefill.painPoints,
        // New audience targeting fields (from create-bundle endpoint)
        customer_keywords: customerProfileModalPrefill.customer_keywords || [],
        customer_tones: customerProfileModalPrefill.customer_tones || [],
        customer_rules: customerProfileModalPrefill.customer_rules || [],
      };
    }
    return undefined;
  }, [customerProfileModalPrefill]);

  // Convert editing service area to modal format
  const serviceAreaDraft = useMemo(() => {
    if (!editingServiceArea) {
      // Check for prefill data from copilot
      const prefillName = serviceAreaModalPrefill?.name || '';
      const prefillType = serviceAreaModalPrefill?.type;
      const prefillAddress = serviceAreaModalPrefill?.address || '';
      const prefillRadius = serviceAreaModalPrefill?.radius;
      const prefillGeographic = serviceAreaModalPrefill?.geographic;

      // Handle geographic type prefill
      if (prefillType === 'geographic' && prefillGeographic) {
        return {
          name: prefillName || prefillGeographic.entityName || '',
          type: 'geographic' as const,
          geographic: {
            entityType: prefillGeographic.entityType || 'city',
            entityId: undefined,
            entityName: prefillGeographic.entityName,
            country: 'US', // Default to US
            state: undefined,
            displayName: prefillGeographic.displayName || prefillGeographic.entityName,
          },
        };
      }

      // Default to radius type
      return {
        name: prefillName,
        type: 'radius' as const,
        address: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
          lat: '',
          long: '',
          formatted: prefillAddress,
        },
        radius: prefillRadius ? String(prefillRadius) : '45',
      };
    }

    // Handle geographic type service areas
    if (editingServiceArea.type === 'geographic' && editingServiceArea.geographic) {
      // Build a full display name like "Cape Coral, FL, USA"
      const entityName = editingServiceArea.geographic.entityName || editingServiceArea.name;
      const state = editingServiceArea.geographic.state;
      const country = editingServiceArea.geographic.country || 'US';

      // Construct full display name from parts
      const fullDisplayName = [entityName, state, country]
        .filter(Boolean)
        .join(', ');

      return {
        name: editingServiceArea.name,
        type: 'geographic' as const,
        geographic: {
          entityType: editingServiceArea.geographic.entityType || 'city',
          entityId: editingServiceArea.geographic.entityId || undefined,
          entityName,
          country,
          state: state || undefined,
          displayName: fullDisplayName,
        },
      };
    }

    // Handle radius type service areas (default)
    return {
      name: editingServiceArea.name,
      type: 'radius' as const,
      address: {
        street: editingServiceArea.address?.street || '',
        city: editingServiceArea.address?.city || '',
        state: editingServiceArea.address?.state || '',
        postalCode: editingServiceArea.address?.postalCode || '',
        country: editingServiceArea.address?.country || '',
        lat: String(editingServiceArea.address?.lat || ''),
        long: String(editingServiceArea.address?.long || ''),
        formatted: [
          editingServiceArea.address?.street,
          editingServiceArea.address?.city,
          editingServiceArea.address?.state,
          editingServiceArea.address?.postalCode,
          editingServiceArea.address?.country,
        ]
          .filter(Boolean)
          .join(', '),
      },
      radius: String(editingServiceArea.radius || 45),
    };
  }, [editingServiceArea, serviceAreaModalPrefill]);

  return (
    <>
      <LocationModal
        isOpen={locationModalOpen}
        onClose={() => closeLocationModal(false)}
        onSave={handleSaveLocation}
        locationDraft={locationDraft}
        isSavingLocation={false}
        editingLocationIdx={editingLocation ? 0 : null}
      />

      <ServiceAreaModal
        isOpen={serviceAreaModalOpen}
        onClose={() => closeServiceAreaModal(false)}
        onSave={handleSaveServiceArea}
        serviceAreaDraft={serviceAreaDraft}
        editingServiceAreaIdx={editingServiceArea ? 0 : null}
        isSaving={false}
      />

      <ProductFormModal
        isOpen={productModalOpen}
        onClose={() => closeProductModal(false)}
        onSubmit={handleSaveProduct}
        title={editingProduct ? 'Edit Product' : 'Add Product'}
        catalogKinds={catalogKinds}
        initialValues={productInitialValues}
      />

      {/* Log editingCustomerProfile when rendering */}
      {customerProfileModalOpen && console.log('🟢🟢🟢 [ChatWithModals] Rendering CustomerProfileFormModal with editingCustomerProfile:',
        editingCustomerProfile ? JSON.stringify({
          _id: editingCustomerProfile._id,
          name: editingCustomerProfile.name,
          customer_rules: editingCustomerProfile.customer_rules,
          customer_tones: editingCustomerProfile.customer_tones,
          hasRules: !!editingCustomerProfile.customer_rules?.length,
          hasTones: !!editingCustomerProfile.customer_tones?.length,
        }, null, 2) : 'null'
      )}
      <CustomerProfileFormModal
        isOpen={customerProfileModalOpen}
        onClose={() => closeCustomerProfileModal(false)}
        onSuccess={handleCustomerProfileSuccess}
        editingProfile={editingCustomerProfile as ICustomerProfile | null || undefined}
        initialProfile={customerProfileInitialValues}
      />

      <DeleteConfirmationModal
        isOpen={deleteConfirmModalOpen}
        onClose={() => setDeleteConfirmModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
        title="Delete Customer Profile"
        itemName={profileToDelete?.name}
        itemType="customer profile"
        confirmButtonText="Delete"
        confirmButtonColorScheme="secondary"
      />

      {children}
    </>
  );
};

export default ChatWithModals;
